/* global dataLayer */

/**
 * Common - DataLayer module
*/

// Imports
import GlobalVars from './GlobalVars';
import {
  isEmpty,
  type,
} from './Utils';
import {
  cartItemAddedEventName,
  wishlistItemAddedEventName,
  productThumbnailClickedEventName,
  algoliaProductThumbnailClickedEventName,
  productListShowMoreEventName,
  algoliaProductListShownEventName,
  productListSortByEventName,
  productListFilterByEventName,
  algoliaProductListFilterByEventName,
  algoliaProductListSortByEventName,
  storeLocMarkerChangeEventName,
  tablistTabChangeEventName,
  samplesAddedEventName,
  productPictureChangeEventName,
  checkoutOptionEventName,
  checkStockInStoreClickEventName,
  scrollToProductListEventName,
  skinDiagnosticCompletedEventName,
  skinCareDiagnosticPageviewEventName,


  socialFollowEventName,
  backInStockNotificationEventName,
  readReviewsEventName,
  submitReviewEventName,
  removeFromCartEventName,
  productSocialViralityEventName,
  promotionViewEventName,
  promotionClickEventName,
  registerCallback as registerEventCallback,
  beautyProfileCompletedEventName,
  expressDeliverySelectionEligibilityEventName,
  expressDeliverySelectionIneligibilityEventName
} from './Events';
import {
  createPushOnLink,
} from './DataLayerNextPage';

/**
 * Action name for when
 * an item has just been added to the cart.
 * @type {String}
*/
const addToCartAction = 'addToCart';

/**
 * Action name for when
 * an item has just been removed from the cart.
 * @type {String}
*/
// const removeFromCartAction = 'removeProduct';

/**
 * Action name for when
 * an item has just been added to the wishlist.
 * @type {String}
*/
// const addToWishlistAction = 'addToWishlist';

/**
 * Action name for when
 * a product thumbnail from a product list
 * has just been clicked.
 * @type {String}
*/
const productThumbnailClickAction = 'eeListClick';

/**
 * Action name for when
 * more products are shown for the product list.
 * @type {String}
*/
const productListShowMoreAction = 'eeListView';

/**
 * Action name for when
 * product list is shown.
 * @type {String}
*/
const productListShownAction = 'eeListView';

/**
 * Action name for when
 * a sort condition has just been chosen for the product list.
 * @type {String}
*/
const productListSortByAction = 'eeListView';

/**
 * Action name for when
 * a filter option has just been selected/unselected for the product list.
 * @type {String}
*/
const productListFilterByAction = 'eeListView';

/**
 * Action name for when
 * a new tab has just been selected
 * from the product description Tablist widget.
 * @type {String}
*/
const productDescTabChangeAction = 'productTabClicked';

/**
 * Action name for when
 * a new store marker has just been clicked
 * from the store locator shops list.
 * @type {String}
*/
const storeMarkerClickedAction = 'boutiqueClicked';

/**
 * Action name for when
 * a new samples is clicked to add in the cart
 * from the free samples checkout section.
 * @type {String}
*/
const samplesAddedAction = 'SamplesAdded';

/**
 * Action name for when
 * delivery page loads through login / register.
 * @type {String}
*/
const checkoutTypeOptionAction = 'eeCheckoutOption';

/**
 * Adds a new entry to the datalayer (after an event has triggered).
 *
 * @param {Mixed} amEntry - The new entry to insert into the datalayer.
*/
function addToDataLayer(amEntry) {
  if ((typeof dataLayer !== 'undefined') && !isEmpty(dataLayer) && (type(dataLayer) === 'array')) {
    dataLayer.push(amEntry);
  }
}

/**
 * Handles the event fired with the 'addToBasket' action.
 * @param {Object} aoEvent       - Event.
 * @param {Array}  aaProductData - Product data as an array of objects.
 */
function handleAddToCart(aoEvent, aaProductData) {
  if (
    isEmpty(aaProductData) ||
    (type(aaProductData) !== 'array')
  ) {
    return;
  }

  addToDataLayer({
    event: addToCartAction,
    currencyCode: GlobalVars.CurrencyCode || '',
    products: aaProductData,
  });
}

/**
 * Handles the event fired with the 'removeProduct' action.
 * @param {Object} aoEvent       - Event.
 * @param {Array}  aaProductData - Product data as an array of objects.
 * @param {String} aaProductList[].sku   - SKU
 * @param {String} aaProductList[].id    - Article ID
 * @param {String} aaProductList[].title - Title
 * @param {String} aaProductList[].price - Price
 * @param {Number} [aaProductList[].qty] - Quantity
 * @param {String} aaProductList[].id           - Article ID
 * @param {String} aaProductList[].category     - Category
 * @param {String} aaProductList[].price        - Price
 * @param {String} aaProductList[].name         - Name
 * @param {String} aaProductList[].brand        - brand
 * @param {Number} aaProductList[].qty          - Quantity
 * @param {Number} aaProductList[].variant      - Variant
 * @param {Number} aaProductList[].productClass - ProductClass
 */
function handleRemoveFromCart(aoEvent, aaProductData) {
  if (isEmpty(aaProductData) || (type(aaProductData) !== 'array')) {
    return;
  }

  addToDataLayer({
    event: removeFromCartEventName,
    currencyCode: GlobalVars.CurrencyCode || '',
    removeFromCartType: 'Cart Hover',
    products: aaProductData,
  });
}

/**
 * Handles the event fired with the 'eeListView' action.
 * @param {Object} aoEvent               - Event.
 * @param {Object} aaProductData         - Product data object.
 * @param {String} aoProductData.id      - SKU for the product.
 * @param {String} aoProductData.name    - Name of the product.
 * @param {String} aoProductData.variant - Variant for the product.
 * @param {String} aoProductData.price   - Price for the product.
 */
function handleProductThumbnailClick(aoEvent, aoProductData) {
  if (isEmpty(aoProductData)) {
    return;
  }

  createPushOnLink({
    event: productThumbnailClickAction,
    products: [{
      id: aoProductData.id,
      name: aoProductData.name,
      price: aoProductData.price,
      position: aoProductData.position,
      list: aoProductData.list,
      brand: (aoProductData.brand != null && aoProductData.brand.length > 0) ? aoProductData.brand : 'None',
      category: aoProductData.category,
      variant: aoProductData.variant,
      productClass: aoProductData.productClass,
    }],
  });
}

/**
 * Handles the event fired with the 'eeListClick' action.
 * @param {Object} aoEvent               - Event.
 * @param {Object} aaProductData         - Product data object.
 * @param {String} aoProductData.id      - SKU for the product.
 * @param {String} aoProductData.name    - Name of the product.
 * @param {String} aoProductData.variant - Variant for the product.
 * @param {String} aoProductData.price   - Price for the product.
 */
function handleAlgoliaProductThumbnailClick(aoEvent, aoProductData, aoQuery) {
  if (isEmpty(aoProductData)) {
    return;
  }

  createPushOnLink({
    event: productThumbnailClickAction,
    siteSearchKeyword: aoQuery,
    products: [{
      position: aoProductData.position,
      id: aoProductData.id,
      category: aoProductData.category,
      price: aoProductData.price,
      brand: (aoProductData.brand != null && aoProductData.brand.length > 0) ? aoProductData.brand : 'None',
      name: aoProductData.name,
      variant: aoProductData.variant,
      productClass: aoProductData.productClass,
      list: aoProductData.list,
    }],
  });
}

/**
 * Handles the event fired with the 'eeListView' action.
 * @param {Object} aoEvent       - Event.
 * @param {Array}  aaProductData - Product data as an array of objects.
 * @param {String} aaProductList[].sku   - SKU
 * @param {String} aaProductList[].id    - Article ID
 * @param {String} aaProductList[].title - Title
 * @param {String} aaProductList[].price - Price
 */
function handleProductListShowMore(aoEvent, aaProductData) {
  if (
    isEmpty(aaProductData) ||
    (type(aaProductData) !== 'array')
  ) {
    return;
  }

  const productsDataList = [];

  for (let i = 0; i < aaProductData.length; i += 1) {
    const dataItem = aaProductData[i];

    productsDataList.push({
      id: dataItem.id,
      name: dataItem.name,
      price: dataItem.price,
      position: dataItem.position,
      list: dataItem.list,
      brand: (dataItem.brand != null && dataItem.brand.length > 0) ? dataItem.brand : 'None',
      category: dataItem.category,
      variant: dataItem.variant,
      productClass: dataItem.productClass,
    });
  }

  addToDataLayer({
    event: productListShowMoreAction,
    currencyCode: GlobalVars.CurrencyCode || '',
    products: productsDataList,
  });
}

/**
 * Handles the event fired with the 'eeListView' action in instant search panel.
 * @param {Object} aoEvent       - Event.
 * @param {Array}  aaProductData - Product data as an array of objects.
 * @param {String} aaProductList[].sku   - SKU
 * @param {String} aaProductList[].id    - Article ID
 * @param {String} aaProductList[].title - Title
 * @param {String} aaProductList[].price - Price
 */
function handleAlgoliaProductListShown(aoEvent, aaProductData, aoQuery, nextPage, isLandingSearch) {
  if (
    isEmpty(aaProductData) ||
    (type(aaProductData) !== 'array')
  ) {
    return;
  }

  const productsDataList = [];

  for (let i = 0; i < aaProductData.length; i += 1) {
    const dataItem = aaProductData[i];

    productsDataList.push({
      id: dataItem.id,
      name: dataItem.name,
      price: dataItem.price,
      position: dataItem.position,
      list: dataItem.list,
      brand: (dataItem.brand != null && dataItem.brand.length > 0) ? dataItem.brand : 'None',
      category: dataItem.category,
      variant: dataItem.variant,
      productClass: dataItem.productClass,
    });
  }

  const datasToPush = {
    event: productListShownAction,
    currencyCode: GlobalVars.CurrencyCode || '',
    siteSearchKeyword: aoQuery,
    kw55: aoQuery,
    cat55: 'Quick Search',
    siteSearchResults: productsDataList.length,
    products: productsDataList,
  };

  if (nextPage && !isLandingSearch) {
    createPushOnLink(datasToPush);
  } else {
    addToDataLayer(datasToPush);
  }
}

/**
 * Handles a 'virtualPageview' event for the product list.
 * @param {Object} aoEvent                - Event.
 * @param {Array}  aaProductData          - Product data as an array of objects.
 * @param {String} aaProductList[].sku    - SKU
 * @param {String} aaProductList[].id     - Article ID
 * @param {String} aaProductList[].title  - Title
 * @param {String} aaProductList[].price  - Price
 * @param {Array}  aoContextData          - Object of context data.
 * @param {String} aoContextData.action   - Action name.
 * @param {String} aoContextData.filters  - Product list filters.
 * @param {String} aoContextData.sortType - Product list sort type.
 */
function handleProductListVPV(aoEvent, aaProductData, aoContextData) {
  if (
    isEmpty(aaProductData) ||
    (type(aaProductData) !== 'array')
  ) {
    return;
  }

  const productsDataList = [];

  for (let i = 0; i < aaProductData.length; i += 1) {
    const dataItem = aaProductData[i];

    productsDataList.push({
      id: dataItem.id,
      name: dataItem.name,
      price: dataItem.price,
      position: dataItem.position,
      list: dataItem.list,
      brand: (dataItem.brand != null && dataItem.brand.length > 0) ? dataItem.brand : 'None',
      category: dataItem.category,
      variant: dataItem.variant,
      productClass: dataItem.productClass,
    });
  }

  let pagePath = window.location.pathname;

  // GA virtual pageview
  if (aoContextData.filters && aoContextData.filters.length) {
    pagePath += '/filters';
  }

  if (aoContextData.sortType && aoContextData.sortType.length) {
    pagePath += `?sort_type=${aoContextData.sortType}`;
  }

  addToDataLayer({
    event: 'virtualPageview',
    eeAction: aoContextData.action,
    page: pagePath,
    filterTypeValue: aoContextData.filters || '',
    currencyCode: GlobalVars.CurrencyCode || '',
    products: productsDataList,
  });
}


/**
 * Handles a 'virtualPageview' event for the product list (for sort by).
 * @param {Object} aoEvent                - Event.
 * @param {Array}  aaProductData          - Product data as an array of objects.
 * @param {String} aaProductList[].sku    - SKU
 * @param {String} aaProductList[].id     - Article ID
 * @param {String} aaProductList[].title  - Title
 * @param {String} aaProductList[].price  - Price
 * @param {Array}  aoContextData          - Object of context data.
 * @param {String} aoContextData.action   - Action name.
 * @param {String} aoContextData.filters  - Product list filters.
 * @param {String} aoContextData.sortType - Product list sort type.
 */
function algoliaHandleSortByProductListVPV(aoEvent, aaProductData, aoContextData) {
  if (
    isEmpty(aaProductData) ||
    (type(aaProductData) !== 'array')
  ) {
    return;
  }

  const productsDataList = [];

  for (let i = 0; i < aaProductData.length; i += 1) {
    const dataItem = aaProductData[i];

    productsDataList.push({
      id: dataItem.id,
      name: dataItem.name,
      price: dataItem.price,
      position: dataItem.position,
      brand: (dataItem.brand != null && dataItem.brand.length > 0) ? dataItem.brand : 'None',
      category: dataItem.category,
      variant: dataItem.variant,
      productClass: dataItem.productClass,
      list: dataItem.list,
    });
  }

  let pagePath = window.location.pathname;

  // GA virtual pageview
  if (aoContextData.filters && aoContextData.filters.length) {
    pagePath += '/filters';
  }

  if (aoContextData.sortType && aoContextData.sortType.length) {
    pagePath += `?sort_type=${aoContextData.sortType}`;
  }

  addToDataLayer({
    event: 'eeListView',
    sortBy: aoContextData.sortType,
    filterTypeValue: aoContextData.filters || '',
    page: pagePath,
    siteSearchKeyword: aoContextData.siteSearchKeyword,
    products: productsDataList,
  });
}

/**
 * Handles a 'virtualPageview' event for the product list. (for filters)
 * @param {Object} aoEvent                - Event.
 * @param {Array}  aaProductData          - Product data as an array of objects.
 * @param {String} aaProductList[].sku    - SKU
 * @param {String} aaProductList[].id     - Article ID
 * @param {String} aaProductList[].title  - Title
 * @param {String} aaProductList[].price  - Price
 * @param {Array}  aoContextData          - Object of context data.
 * @param {String} aoContextData.action   - Action name.
 * @param {String} aoContextData.filters  - Product list filters.
 * @param {String} aoContextData.sortType - Product list sort type.
 */
function algoliaHandleFiltersProductListVPV(aoEvent, aaProductData, aoContextData) {
  if (
    isEmpty(aaProductData) ||
    (type(aaProductData) !== 'array')
  ) {
    return;
  }

  const productsDataList = [];

  for (let i = 0; i < aaProductData.length; i += 1) {
    const dataItem = aaProductData[i];

    productsDataList.push({
      id: dataItem.id,
      name: dataItem.name,
      price: dataItem.price,
      position: dataItem.position,
      brand: (dataItem.brand != null && dataItem.brand.length > 0) ? dataItem.brand : 'None',
      category: dataItem.category,
      variant: dataItem.variant,
      productClass: dataItem.productClass,
      list: dataItem.list,
    });
  }

  let pagePath = window.location.pathname;

  // GA virtual pageview
  if (aoContextData.filters && aoContextData.filters.length) {
    pagePath += '/filters';
  }

  if (aoContextData.sortType && aoContextData.sortType.length) {
    pagePath += `?sort_type=${aoContextData.sortType}`;
  }

  addToDataLayer({
    event: 'eeListView',
    filterSelection: aoContextData.filterSelection,
    page: pagePath,
    filterTypeValue: aoContextData.filters || '',
    currencyCode: GlobalVars.CurrencyCode || '',
    siteSearchKeyword: aoContextData.siteSearchKeyword,
    products: productsDataList,
  });
}

/**
 * Handles the event fired with the 'eeListView' action (VPV for sort by).
 * @param {Object} aoEvent                - Event.
 * @param {Array}  aaProductData          - Product data as an array of objects.
 * @param {String} aaProductList[].sku    - SKU
 * @param {String} aaProductList[].id     - Article ID
 * @param {String} aaProductList[].title  - Title
 * @param {String} aaProductList[].price  - Price
 * @param {Array}  aoContextData          - Object of context data.
 * @param {String} aoContextData.filters  - Product list filters.
 * @param {String} aoContextData.sortType - Product list sort type.
 */
function handleProductListSortBy(aoEvent, aaProductData, aoContextData) {
  const contextData = aoContextData;

  contextData.action = productListSortByAction;

  handleProductListVPV(aoEvent, aaProductData, contextData);
}

/**
 * Handles the event fired with the 'eeListView' action (VPV for sort by).
 * @param {Object} aoEvent                - Event.
 * @param {Array}  aaProductData          - Product data as an array of objects.
 * @param {String} aaProductList[].sku    - SKU
 * @param {String} aaProductList[].id     - Article ID
 * @param {String} aaProductList[].title  - Title
 * @param {String} aaProductList[].price  - Price
 * @param {Array}  aoContextData          - Object of context data.
 * @param {String} aoContextData.filters  - Product list filters.
 * @param {String} aoContextData.sortType - Product list sort type.
 */
function algoliaHandleProductListSortBy(aoEvent, aaProductData, aoContextData) {
  const contextData = aoContextData;

  contextData.action = productListSortByAction;

  algoliaHandleSortByProductListVPV(aoEvent, aaProductData, contextData);
}

/**
 * Handles the event fired with the 'eeListView' action (VPV for filters).
 * @param {Object} aoEvent                - Event.
 * @param {Array}  aaProductData          - Product data as an array of objects.
 * @param {String} aaProductList[].sku    - SKU
 * @param {String} aaProductList[].id     - Article ID
 * @param {String} aaProductList[].title  - Title
 * @param {String} aaProductList[].price  - Price
 * @param {Array}  aoContextData          - Object of context data.
 * @param {String} aoContextData.filters  - Product list filters.
 * @param {String} aoContextData.sortType - Product list sort type.
 */
function handleProductListFilterBy(aoEvent, aaProductData, aoContextData) {
  const contextData = aoContextData;

  contextData.action = productListFilterByAction;

  handleProductListVPV(aoEvent, aaProductData, contextData);
}

/**
 * Handles the event fired with the 'eeListView' action (VPV for filters).
 * @param {Object} aoEvent                - Event.
 * @param {Array}  aaProductData          - Product data as an array of objects.
 * @param {String} aaProductList[].sku    - SKU
 * @param {String} aaProductList[].id     - Article ID
 * @param {String} aaProductList[].title  - Title
 * @param {String} aaProductList[].price  - Price
 * @param {Array}  aoContextData          - Object of context data.
 * @param {String} aoContextData.filters  - Product list filters.
 * @param {String} aoContextData.sortType - Product list sort type.
 */
function algoliaHandleProductListFilterBy(aoEvent, aaProductData, aoContextData) {
  const contextData = aoContextData;

  contextData.action = productListFilterByAction;

  algoliaHandleFiltersProductListVPV(aoEvent, aaProductData, contextData);
}

/**
 * Handles the event fired for the 'tablist tab change' event.
 * @param {Object} aoEvent            - Event.
 * @param {Object} aoTablistContainer - Tablist container.
 * @param {Object} aoTablistContainer.productname - Product name.
 * @param {Object} aoTablistContainer.tabname - Tab name.
 * @param {Object} aoTablistContainer.productname - Product id.
 */
function handleTablistTabChange(aoEvent, aoTablistContainer) {
  if (aoTablistContainer) {
    addToDataLayer({
      event: productDescTabChangeAction,
      productName: aoTablistContainer.productname,
      productTabName: aoTablistContainer.tabname,
      productId: aoTablistContainer.productid,
    });
  }
}

/**
 * Handles the event fired for the 'tablist tab change' event.
 * @param {Object} aoEvent            - Event.
 * @param {Object} aoStoreData - Object of store data.
 */
function handleStoreLocMarkerChange(aoEvent, aoStoreData) {
  addToDataLayer({
    event: storeMarkerClickedAction,
    boutiqueName: aoStoreData.name,
    boutiqueId: aoStoreData.id,
  });
}

/**
 * Handles the event fired for the 'samplesAdded' event.
 * @param {Object} aoEvent            - Event.
 * @param {Object} aoSamplesData      - Object of samples data.
 */
function handleSamplesAdded(aoEvent, aoSamplesData) {
  if (aoSamplesData) {
    addToDataLayer({
      event: samplesAddedAction,
      productName: aoSamplesData.title,
      productId: aoSamplesData.sku,
    });
  }
}

/**
 * Handles the event fired for the 'checkoutOption' event.
 * @param {Object} aoEvent               - Event.
 * @param {Object} aoCheckoutOption      - Object of checkout option data.
 * @param {Object} aoCheckoutOption.action      - Event Action
 * @param {Object} aoCheckoutOption.step        - Step
 * @param {Object} aoCheckoutOption.option      - Option
 */
function handleCheckoutOption(aoEvent, aoCheckoutOption) {
  if (aoCheckoutOption) {
    const dataLayerObj = {
      event: checkoutTypeOptionAction,
      checkoutStep: aoCheckoutOption.step,
      checkoutOption: aoCheckoutOption.option,
    };
    if (aoCheckoutOption.action != null) {
      dataLayerObj.eventAction = aoCheckoutOption.action;
    }
    addToDataLayer(dataLayerObj);
  }
}

/**
 * Handles the event fired for the 'productPictureChange' event.
 * @param {Object} aoEvent            - Event.
 * @param {Object} aoProductData      - Object of product data.
 */
function handleProductPictureChange(aoEvent, aoProductData) {
  if (aoProductData) {
    addToDataLayer({
      event: productPictureChangeEventName,
      productName: aoProductData.name,
      productId: aoProductData.id,
      productPictureID: aoProductData.imgId
    });
  }
}

/**
 * Handles the event fired for the 'checkStockInStore' event.
 * @param {Object} aoEvent            - Event.
 * @param {Object} aoProductData      - Object of product data.
 */
function handleCheckStockInStoreClick(aoEvent, aoProductData) {
  if (aoProductData) {
    addToDataLayer({
      event: checkStockInStoreClickEventName,
      productName: aoProductData.name,
      productId: aoProductData.id,
    });
  }
}

function handleScrollToProductList(aoEvent, aoProductData) {
  if (aoProductData && Array.isArray(aoProductData)) {
    addToDataLayer({
      currencyCode: GlobalVars.CurrencyCode || '',
      event: 'eeListView',
      products: aoProductData
    });
  }
}

/**
 * Handles the event fired for the
 */
function handleSkinDiagnosticCompleted(aoEvent, asSkinDiagnosticData) {
  if (asSkinDiagnosticData) {
    addToDataLayer({
      event: skinDiagnosticCompletedEventName,
      skinDiagnosticProfile: asSkinDiagnosticData,
    });
  }
}

/**
 * Handles the event fired for the skin diag steps
 */
function handleSkinCareDiagnosticPageview(aoEvent, asStepNumber) {
  if (asStepNumber) {
    addToDataLayer({
      event: skinCareDiagnosticPageviewEventName,
      page: `skin-diagnostic/${asStepNumber}`,
    });
  }
}

/**
 * Handles the event fired for the skin diag steps
 */
function handlebeautyProfileCompletedClick(aoEvent, data) {
  if (data) {
    createPushOnLink({
      event: beautyProfileCompletedEventName,
      beautyProfileCategory: data.beautyProfileCategory,
      userBeautyProfile: data.userBeautyProfile
    });
  }
}

/**
 * Handles the event fired for the promotion module view
 * @param {Object} aoEvent               - Event.
 * @param {Object} data                  - Promotions data.
 */
function handlePromotionPageview(aoEvent, data) {
  if (data) {
    addToDataLayer({
      event: promotionViewEventName,
      promotions: data
    });
  }
}

/**
 * Handles the event fired for the promotion module click
 * @param {Object} aoEvent               - Event.
 * @param {Object} data                  - Promotions data.
 */
function handlePromotionClick(aoEvent, data) {
  if (isEmpty(data)) {
    return;
  }

  createPushOnLink({
    event: promotionClickEventName,
    promotions: data
  });
}

/**


 * Handles the event fired for the 'socialFollow' event.
 * @param {Object} aoSocialNetworkData      - Object of social network data.
 */
function handleSocialFollowClick(aoEvent, aoSocialNetworkData) {
  if (aoSocialNetworkData) {
    addToDataLayer({
      event: socialFollowEventName,
      socialNetwork: aoSocialNetworkData.name,
      socialInteraction: aoSocialNetworkData.interaction,
    });
  }
}

/**
 * Handles the event fired for the 'productSocialVirality' event.
 * @param {Object} aaProductSocialData      - Object of social network data.
 */
function handleProductSocialViralityCheck(aoEvent, aoProductSocialData) {
  if (aoProductSocialData) {
    addToDataLayer({
      event: productSocialViralityEventName,
      socialNetwork: aoProductSocialData.name,
      productName: aoProductSocialData.product,
      productId: aoProductSocialData.sku,
    });
  }
}

/**
 * Handles the event fired for the 'backInStockNotification' event
 */
function handleBackInStockNotificationSubmitted(aoEvent, aoProductData) {
  if (aoProductData) {
    addToDataLayer({
      event: backInStockNotificationEventName,
      productName: aoProductData.name,
      productId: aoProductData.id,
    });
  }
}

/**
 * Handles the event fired for the 'readReviews' event
 */
function handleReadReviewsClicked(aoEvent, aoProductData) {
  if (aoProductData) {
    addToDataLayer({
      event: readReviewsEventName,
      productName: aoProductData.name,
      productId: aoProductData.id,
    });
  }
}

/**
 * Handles the event fired for the 'reviews submit' event
 */
function handleReviewSubmitted(aoEvent, aoProductData) {
  if (aoProductData) {
    addToDataLayer({
      event: submitReviewEventName,
      productName: aoProductData.name,
      productId: aoProductData.id,
    });
  }
}

/**
 * Handles the event fired for the 'reviews submit' event
 */
function handleAddToWishlist(aoEvent, aoProductData) {
  if (aoProductData) {
    addToDataLayer({
      event: wishlistItemAddedEventName,
      productName: aoProductData.name,
      productId: aoProductData.id,
    });
  }
}

/**
 * Handles the event fired for the 'Express Delivery Selection' event
 */
function handleExpressDeliverySelection(aoEvent, aoShippingData) {
  if (aoShippingData && aoShippingData.ineligibility !== undefined) {
    addToDataLayer({
      event: expressDeliverySelectionIneligibilityEventName,
      expressDeliveryIneligibility: aoShippingData.ineligibility,
      expressDeliveryMethodSelected: aoShippingData.shippingmethod,
    });
  } else if (aoShippingData) {
    addToDataLayer({
      event: expressDeliverySelectionEligibilityEventName,
      expressDeliveryMethodSelected: aoShippingData.shippingmethod,
    });
  }
}

/**
 * Sets event handlers.
 */
function setEventHandlers() {
  // Register callback for 'addToCart' action
  registerEventCallback({
    target: document,
    events: cartItemAddedEventName,
    handler: handleAddToCart,
  });

  // Register callback for 'removeProduct' action
  // registerEventCallback({
  //   target: document,
  //   events: cartItemRemovedEventName,
  //   handler: handleRemoveFromCart,
  // });
  registerEventCallback({
    target: document,
    events: removeFromCartEventName,
    handler: handleRemoveFromCart,
  });

  // Register callback for 'addToWishlist' action
  // registerEventCallback({
  //   target: document,
  //   events: wishlistItemAddedEventName,
  //   handler: handleAddToWishlist,
  // });
  registerEventCallback({
    target: document,
    events: wishlistItemAddedEventName,
    handler: handleAddToWishlist,
  });

  // Register callback for 'eeListClick' action
  registerEventCallback({
    target: document,
    events: productThumbnailClickedEventName,
    handler: handleProductThumbnailClick,
  });

  // Register callback for 'eeListClick' action
  registerEventCallback({
    target: document,
    events: algoliaProductThumbnailClickedEventName,
    handler: handleAlgoliaProductThumbnailClick,
  });

  // Register callback for 'eeListView' action
  registerEventCallback({
    target: document,
    events: productListShowMoreEventName,
    handler: handleProductListShowMore,
  });

  // Register callback for 'eeListView' action
  registerEventCallback({
    target: document,
    events: algoliaProductListShownEventName,
    handler: handleAlgoliaProductListShown,
  });

  // Register callback for 'eeListView' action (VPV for sort by)
  registerEventCallback({
    target: document,
    events: productListSortByEventName,
    handler: handleProductListSortBy,
  });

  // Register callback for 'eeListView' action (VPV for sort by algolia)
  registerEventCallback({
    target: document,
    events: algoliaProductListSortByEventName,
    handler: algoliaHandleProductListSortBy,
  });

  // Register callback for 'eeListView' action (VPV for filters)
  registerEventCallback({
    target: document,
    events: productListFilterByEventName,
    handler: handleProductListFilterBy,
  });

  // Register callback for 'eeListView' action (VPV for filters)
  registerEventCallback({
    target: document,
    events: algoliaProductListFilterByEventName,
    handler: algoliaHandleProductListFilterBy,
  });

  // Register callback for 'tablist tab change' event
  registerEventCallback({
    target: document,
    events: tablistTabChangeEventName,
    handler: handleTablistTabChange,
  });

  // Register callback for 'storelocator marker change' event
  registerEventCallback({
    target: document,
    events: storeLocMarkerChangeEventName,
    handler: handleStoreLocMarkerChange,
  });

  // Register callback for 'samplesAdded' action
  registerEventCallback({
    target: document,
    events: samplesAddedEventName,
    handler: handleSamplesAdded,
  });

  // Register callback for 'productPictureChange' action
  registerEventCallback({
    target: document,
    events: productPictureChangeEventName,
    handler: handleProductPictureChange,
  });

  // Register callback for 'checkoutOption' action
  registerEventCallback({
    target: document,
    events: checkoutOptionEventName,
    handler: handleCheckoutOption,
  });

  // Register callback for 'checkoutOption' action
  registerEventCallback({
    target: document,
    events: checkStockInStoreClickEventName,
    handler: handleCheckStockInStoreClick,
  });


  // Register callback for 'scrollToProductList' action
  registerEventCallback({
    target: document,
    events: scrollToProductListEventName,
    handler: handleScrollToProductList
  });

  // Register callback for 'skinDiagnosticCompleted' action
  registerEventCallback({
    target: document,
    events: skinDiagnosticCompletedEventName,
    handler: handleSkinDiagnosticCompleted,
  });

  // Register callback for 'skinCareDiagnosticPageview' action
  registerEventCallback({
    target: document,
    events: skinCareDiagnosticPageviewEventName,
    handler: handleSkinCareDiagnosticPageview,
  });


  // Register callback for 'eeListClick' action
  registerEventCallback({
    target: document,
    events: beautyProfileCompletedEventName,
    handler: handlebeautyProfileCompletedClick,
  });

  // Register callback for 'promotionPageview' action
  registerEventCallback({
    target: document,
    events: promotionViewEventName,
    handler: handlePromotionPageview,
  });

  // Register callback for 'promotionClickEvent' action
  registerEventCallback({
    target: document,
    events: promotionClickEventName,
    handler: handlePromotionClick,
  });

  // Register callback for 'socialFollow' event
  registerEventCallback({
    target: document,
    events: socialFollowEventName,
    handler: handleSocialFollowClick,
  });

  // Register callback for 'productSocialVirality' event
  registerEventCallback({
    target: document,
    events: productSocialViralityEventName,
    handler: handleProductSocialViralityCheck,
  });

  // Register callback for 'backInStockNotificationSubmitted' action
  registerEventCallback({
    target: document,
    events: backInStockNotificationEventName,
    handler: handleBackInStockNotificationSubmitted,
  });

  // Register callback for 'readReviews' action
  registerEventCallback({
    target: document,
    events: readReviewsEventName,
    handler: handleReadReviewsClicked,
  });

  // Register callback for 'reviewSubmitted' action
  registerEventCallback({
    target: document,
    events: submitReviewEventName,
    handler: handleReviewSubmitted,
  });

  // Register callback for 'Express Delivery Selection' action
  registerEventCallback({
    target: document,
    events: expressDeliverySelectionEligibilityEventName,
    handler: handleExpressDeliverySelection,
  });
}

function init() {
  // Set event handlers
  setEventHandlers();
}

// Named exports
export {
  init,
  addToDataLayer,
};

export default addToDataLayer;



// WEBPACK FOOTER //
// ./js/common/DataLayer.js